<template>
  <v-dialog
    v-model="dialogVisible"
    :max-width="modalLayout.XL"
    scrollable
    v-bind="modalPolicy.P2_FORM"
    content-class="solvit-dialog-form-shell"
    @keydown.esc="dialogVisible = false"
  >
    <!-- 높이 고정: 그룹마다 멤버 수가 달라도 모달 크기가 흔들리지 않게 height 사용 -->
    <v-card
      class="d-flex flex-column"
      :style="{ height: modalMaxHeight }"
    >
      <CrudFormDialogHeader
        :kicker="$t('map.toolbarCaptionAi')"
        :title="$t('map.aiDedupTitle')"
        show-close
        @close="dialogVisible = false"
      />
      <v-progress-linear
        :active="loading"
        :indeterminate="loading"
        absolute
        color="primary"
      />

      <v-card-text class="pa-0 flex-grow-1 d-flex flex-column ai-dedup-body overflow-hidden">
        <p class="text-caption text--secondary px-3 pt-2 mb-0 flex-shrink-0">
          {{ $t('map.aiDedupIntro') }}
        </p>

        <template v-if="!loading">
          <div class="px-3 pb-2 flex-shrink-0 ai-dedup-toolbar">
            <div class="d-flex align-center flex-wrap ai-dedup-controls">
              <div class="ai-dedup-threshold-wrap flex-grow-0">
                <div class="text-caption text--secondary mb-1">
                  {{ $t('map.aiDedupThreshold') }}
                </div>
                <v-select
                  v-model="thresholdMeters"
                  :items="thresholdOptions"
                  dense
                  outlined
                  hide-details
                  class="ai-dedup-threshold"
                />
              </div>
              <v-spacer />
              <template v-if="groups.length > 0">
                <v-btn
                  small
                  outlined
                  class="solvit-crud-outline-btn"
                  :disabled="groupIndex <= 0"
                  @click="groupIndex -= 1"
                >
                  <v-icon left small>mdi-chevron-left</v-icon>
                  {{ $t('map.aiDedupNavPrev') }}
                </v-btn>
                <span class="text-body-2 px-2 ai-dedup-counter">
                  {{ $t('map.aiDedupGroupCounter', { current: groupIndex + 1, total: groups.length }) }}
                </span>
                <v-btn
                  small
                  outlined
                  class="solvit-crud-outline-btn"
                  :disabled="groupIndex >= groups.length - 1"
                  @click="groupIndex += 1"
                >
                  {{ $t('map.aiDedupNavNext') }}
                  <v-icon right small>mdi-chevron-right</v-icon>
                </v-btn>
              </template>
            </div>

            <div
              v-if="groups.length > 0"
              class="d-flex align-center flex-wrap mt-2 ai-dedup-group-actions"
            >
              <span class="text-caption text--secondary">
                {{ $t('map.aiDedupKeepToggleHint') }}
              </span>
              <v-spacer />
              <v-btn
                small
                outlined
                :color="isGroupAllHidden ? 'warning' : undefined"
                class="solvit-crud-outline-btn"
                @click="toggleGroupNotDefect"
              >
                {{
                  isGroupAllHidden
                    ? $t('map.aiDedupNotDefectRestore')
                    : $t('map.aiDedupNotDefect')
                }}
              </v-btn>
            </div>
          </div>
        </template>

        <div class="ai-dedup-scroll flex-grow-1 px-3 pb-2">
          <div
            v-if="loading"
            class="text-center py-8"
          >
            <v-progress-circular
              indeterminate
              color="primary"
              size="48"
            />
            <div class="text-body-2 text--secondary mt-4">
              {{ $t('table.loading') }}
            </div>
          </div>

          <template v-else>
            <v-alert
              v-if="groups.length === 0"
              type="success"
              outlined
            >
              {{ $t('map.aiDedupNoGroups', { m: thresholdMeters }) }}
            </v-alert>

            <div
              v-else-if="currentGroup"
              class="d-flex flex-wrap justify-center ai-dedup-cards"
            >
              <v-card
                v-for="member in currentGroup.members"
                :key="member.seq"
                outlined
                class="ai-dedup-card"
                :class="{
                  'ai-dedup-card--keeper': isKept(member.seq),
                  'ai-dedup-card--hidden': !isKept(member.seq),
                  'ai-dedup-card--locked': isLastKept(member.seq)
                }"
                @click="toggleKeep(member.seq)"
              >
                <div class="ai-dedup-card__frame">
                  <svg
                    v-if="detailBySeq[member.seq]"
                    class="ai-dedup-card__svg"
                    :viewBox="memberViewBox(member.seq)"
                    preserveAspectRatio="xMidYMid meet"
                  >
                    <image
                      :href="detailBySeq[member.seq].imageUrl"
                      x="0"
                      y="0"
                      width="640"
                      height="512"
                    />
                    <polygon
                      :points="formatPoints(detailBySeq[member.seq].coordinates)"
                      class="ai-dedup-card__bbox"
                    />
                  </svg>
                  <div
                    v-else
                    class="d-flex align-center justify-center fill-height text--secondary text-caption"
                  >
                    <v-icon left small>mdi-image-off-outline</v-icon>
                    {{ member.imgName }}
                  </div>
                </div>
                <div class="px-2 py-1 d-flex align-center ai-dedup-card__meta">
                  <v-tooltip top open-delay="400">
                    <template #activator="{ on, attrs }">
                      <div
                        class="text-caption text-truncate flex-grow-1 min-width-0"
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ memberImageName(member) }}
                      </div>
                    </template>
                    <span>{{ memberImageName(member) }}</span>
                  </v-tooltip>
                  <v-chip
                    v-if="member.seq === currentGroup.recommendedSeq"
                    x-small
                    outlined
                    color="primary"
                    class="ml-1 flex-shrink-0"
                  >
                    {{ $t('map.aiDedupRecommended') }}
                  </v-chip>
                </div>
                <div class="px-2 pb-2">
                  <v-chip
                    v-if="isKept(member.seq)"
                    small
                    color="primary"
                    text-color="white"
                  >
                    {{
                      isLastKept(member.seq)
                        ? $t('map.aiDedupKeepRequired')
                        : $t('map.aiDedupKeepThis')
                    }}
                  </v-chip>
                  <v-chip
                    v-else
                    small
                    outlined
                  >
                    {{ $t('map.aiDedupHiddenBadge') }}
                  </v-chip>
                </div>
              </v-card>
            </div>
          </template>
        </div>
      </v-card-text>

      <v-divider v-if="!loading" />

      <v-card-actions
        v-if="!loading"
        class="px-4 py-3 flex-column align-stretch ai-dedup-footer"
      >
        <span
          v-if="groups.length > 0"
          class="text-body-2 text--secondary mb-1"
        >
          {{
            $t('map.aiDedupProgressLabel', {
              visited: visitedGroupIds.length,
              total: groups.length,
              n: reviewedPlannedHiddenSeqs.length
            })
          }}
        </span>
        <span
          v-if="groups.length > 0 && reviewedPlannedHiddenSeqs.length > 0"
          class="text-caption text--secondary mb-2"
        >
          {{ reviewedDeleteRangeLabel }}
        </span>

        <div class="d-flex align-center flex-wrap ai-dedup-footer-row">
          <span class="text-caption font-weight-medium mr-2 ai-dedup-footer-label">
            {{ $t('map.aiDedupDeleteSection') }}
          </span>
          <v-btn
            small
            outlined
            color="error"
            class="solvit-crud-outline-btn"
            :disabled="groups.length === 0 || reviewedPlannedHiddenSeqs.length === 0"
            @click="deleteReviewedDuplicates"
          >
            {{ $t('map.aiDedupDeleteReviewed') }}
          </v-btn>
          <v-btn
            small
            outlined
            color="error"
            class="solvit-crud-outline-btn"
            :disabled="groups.length === 0"
            @click="deleteAllDuplicates"
          >
            {{ $t('map.aiDedupDeleteAll') }}
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex';
import CrudFormDialogHeader from '@/components/common/CrudFormDialogHeader.vue';
import {
  MODAL_MAX_WIDTH,
  MODAL_CLOSE_POLICY,
  MODAL_FORM_MAX_HEIGHT
} from '@/constants/modalLayout';
import { moduleDefectApi } from '@/apis/module.defect.api';
import DotSolarMap from '@/components/map/DotSolarMap.js';
import DotSolarMapContext from '@/components/map/DotSolarMapContext.js';
import {
  buildDedupGroups,
  computeHiddenSeqs
} from '@/utils/defect/aiDefectDedup.js';
import {
  loadAiDefectDedupState,
  saveAiDefectDedupState,
  saveAiDefectDedupDraft
} from '@/utils/defect/aiDefectDedupStore.js';
import { getDefectShapeCenterPx } from '@/utils/defect/aiDefectDisplayRegion.js';
import { invalidateModuleDefectDetailCache } from '@/utils/moduleDefectDetailCache.js';

const VIEW_W = 640;
const VIEW_H = 512;

function ringCoordinatesFromGeom(geom) {
  if (!geom || !geom.type) return [];
  const c = geom.coordinates;
  if (geom.type === 'Polygon' && Array.isArray(c) && c[0]) {
    return c[0];
  }
  if (geom.type === 'MultiPolygon' && Array.isArray(c) && c[0] && c[0][0]) {
    return c[0][0];
  }
  return [];
}

export default {
  name: 'AiDefectDedupDialog',
  components: {
    CrudFormDialogHeader
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    reportId: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      modalLayout: MODAL_MAX_WIDTH,
      modalPolicy: MODAL_CLOSE_POLICY,
      loading: false,
      /** WFS 지오 행 + ai-detects 상세를 seq로 조인한 검출 목록 */
      detections: [],
      /** seq → { imageUrl, imageName, coordinates, centerDist } */
      detailBySeq: {},
      thresholdMeters: 1.5,
      groups: [],
      groupIndex: 0,
      /** 그룹 id → 지도에 남길 seq 목록 (복수 가능, 최소 1개) */
      keepSeqsByGroupId: {},
      /** 화면에 표시(=검토)한 그룹 id */
      visitedGroupIds: [],
      /** 현재 지도에 적용돼 있는 숨김 seq */
      appliedHiddenSeqs: [],
      hasSavedState: false
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      }
    },
    modalMaxHeight() {
      return MODAL_FORM_MAX_HEIGHT;
    },
    thresholdOptions() {
      return [1, 1.5, 2, 3].map(v => ({ text: `${v} m`, value: v }));
    },
    currentGroup() {
      return this.groups[this.groupIndex] || null;
    },
    centerDistBySeq() {
      const out = {};
      Object.keys(this.detailBySeq).forEach(seq => {
        out[seq] = this.detailBySeq[seq].centerDist;
      });
      return out;
    },
    /** 이전 적용으로 이미 숨김이 반영돼 있는 그룹 id */
    appliedGroupIds() {
      const hidden = new Set(this.appliedHiddenSeqs.map(Number));
      return this.groups
        .filter(g => g.members.some(m => hidden.has(Number(m.seq))))
        .map(g => g.id);
    },
    /**
     * 「검토한 그룹까지 적용」 대상: 검토한 그룹 + 이전에 적용된 그룹.
     * 미검토·미적용 그룹은 계산에서 빠져 마커가 그대로 표시된다.
     */
    reviewedTargetGroupIds() {
      return new Set([...this.visitedGroupIds, ...this.appliedGroupIds]);
    },
    reviewedPlannedHiddenSeqs() {
      return computeHiddenSeqs(
        this.groups,
        this.keepSeqsByGroupId,
        this.reviewedTargetGroupIds
      );
    },
    /** 현재 그룹이 "전체 제외(전원 삭제 대상)" 상태인지 */
    isGroupAllHidden() {
      if (!this.currentGroup) return false;
      const kept = this.keepSeqsByGroupId[this.currentGroup.id];
      return Array.isArray(kept) && kept.length === 0;
    },
    reviewedGroupNumberRange() {
      const numbers = [];
      this.groups.forEach((group, index) => {
        if (this.reviewedTargetGroupIds.has(group.id)) {
          numbers.push(index + 1);
        }
      });
      if (!numbers.length) {
        return { from: 0, to: 0, count: 0 };
      }
      return {
        from: Math.min(...numbers),
        to: Math.max(...numbers),
        count: numbers.length
      };
    },
    reviewedDeleteRangeLabel() {
      const range = this.reviewedGroupNumberRange;
      if (!range.from) {
        return '';
      }
      if (range.from === range.to) {
        return this.$t('map.aiDedupDeleteReviewedRangeSingle', {
          from: range.from,
          n: this.reviewedPlannedHiddenSeqs.length
        });
      }
      return this.$t('map.aiDedupDeleteReviewedRange', {
        from: range.from,
        to: range.to,
        n: this.reviewedPlannedHiddenSeqs.length
      });
    }
  },
  watch: {
    visible(val) {
      if (val && this.reportId) {
        this.fetchData();
      } else {
        this.saveDraft();
        this.resetState();
      }
    },
    thresholdMeters() {
      // fetchData 내부 대입(초안 복원)이 nextTick에 워처를 발동시키는 것 차단
      if (this.loading || this._internalUpdate) return;
      // 임계값이 바뀌면 그룹 id가 달라지므로 검토 진행은 처음부터
      this.visitedGroupIds = [];
      this.groupIndex = 0;
      this.rebuildGroups();
      this.markCurrentGroupVisited();
      this.saveDraft();
    },
    groupIndex() {
      if (this.loading || this._internalUpdate) return;
      this.markCurrentGroupVisited();
      this.saveDraft();
    }
  },
  methods: {
    ...mapActions('commonAlert', ['addAlert']),
    resetState() {
      this.detections = [];
      this.detailBySeq = {};
      this.groups = [];
      this.groupIndex = 0;
      this.keepSeqsByGroupId = {};
      this.visitedGroupIds = [];
      this.appliedHiddenSeqs = [];
      this.hasSavedState = false;
    },
    async fetchData() {
      this.loading = true;
      this._internalUpdate = true;
      try {
        const [geoRows, detectResponse] = await Promise.all([
          DotSolarMapContext.fetchAiDetectDefectGeoRows(this.reportId),
          moduleDefectApi.getAIDetectedDefects(this.reportId)
        ]);

        const detailBySeq = {};
        const images = detectResponse?.content || detectResponse || [];
        (Array.isArray(images) ? images : []).forEach(item => {
          (item.defects || []).forEach(d => {
            const seq = Number(d.id);
            if (!Number.isFinite(seq)) return;
            const center = getDefectShapeCenterPx(d.defectShapeGeom);
            detailBySeq[seq] = {
              imageUrl: item.thermalImageUrl,
              imageName: item.thermalImageName,
              coordinates: ringCoordinatesFromGeom(d.defectShapeGeom),
              centerDist: center
                ? Math.hypot(center.x - VIEW_W / 2, center.y - VIEW_H / 2)
                : Infinity
            };
          });
        });

        this.detailBySeq = detailBySeq;
        // 지오 좌표와 이미지 상세가 모두 있는 검출만 검토 대상
        this.detections = geoRows.filter(r => detailBySeq[r.seq]);

        const saved = loadAiDefectDedupState(this.reportId);
        this.appliedHiddenSeqs = saved.hiddenSeqs;
        this.hasSavedState = saved.hiddenSeqs.length > 0;
        const draft = saved.draft;
        this.thresholdMeters =
          draft?.thresholdMeters ?? saved.thresholdMeters ?? this.thresholdMeters;
        this.rebuildGroups();
        if (draft && draft.thresholdMeters === this.thresholdMeters) {
          this.applyDraft(draft);
        }
        this.markCurrentGroupVisited();
        this.saveDraft();
      } catch (error) {
        console.error('[AiDefectDedupDialog] fetchData', error);
        this.resetState();
        this.addAlert({
          type: 'error',
          message: this.$t('map.aiDuplicateRemovalLoadError'),
          timeout: 6000
        });
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this._internalUpdate = false;
        });
      }
    },
    /**
     * 클러스터링 재계산 + keep 초기값 결정.
     * 이전에 적용된 그룹: 숨김이 아닌 멤버들 / 처음 보는 그룹: 추천 대표 1개.
     */
    rebuildGroups() {
      const hidden = new Set(this.appliedHiddenSeqs.map(Number));
      this.groups = buildDedupGroups(
        this.detections,
        this.thresholdMeters,
        this.centerDistBySeq
      );
      const keepSeqsByGroupId = {};
      this.groups.forEach(group => {
        const memberSeqs = group.members.map(m => Number(m.seq));
        const appliedKeeps = memberSeqs.filter(s => !hidden.has(s));
        const wasApplied = appliedKeeps.length < memberSeqs.length;
        // 이전 적용 그룹은 그 상태 그대로 — 빈 배열이면 "결함 아님(전원 숨김)" 유지
        keepSeqsByGroupId[group.id] = wasApplied
          ? appliedKeeps
          : [Number(group.recommendedSeq)];
      });
      this.keepSeqsByGroupId = keepSeqsByGroupId;
      this.groupIndex = Math.min(
        this.groupIndex,
        Math.max(0, this.groups.length - 1)
      );
    },
    /** 저장된 검토 초안(선택·방문·위치)을 현재 그룹 구조에 맞게 복원 */
    applyDraft(draft) {
      const byId = new Map(this.groups.map(g => [g.id, g]));
      Object.keys(draft.keepSeqsByGroupId).forEach(groupId => {
        const group = byId.get(groupId);
        if (!group) return;
        const rawKeeps = draft.keepSeqsByGroupId[groupId];
        const memberSet = new Set(group.members.map(m => Number(m.seq)));
        const keeps = rawKeeps.map(Number).filter(s => memberSet.has(s));
        // 빈 배열은 의도된 "결함 아님" — 단, 멤버가 바뀌어 전부 걸러진 경우는 기본값 유지
        if (keeps.length > 0 || rawKeeps.length === 0) {
          this.$set(this.keepSeqsByGroupId, groupId, keeps);
        }
      });
      this.visitedGroupIds = draft.visitedGroupIds.filter(id => byId.has(id));
      this.groupIndex = Math.min(
        Math.max(0, draft.groupIndex),
        Math.max(0, this.groups.length - 1)
      );
    },
    markCurrentGroupVisited() {
      const id = this.currentGroup?.id;
      if (!id || this.visitedGroupIds.includes(id)) return;
      this.visitedGroupIds = [...this.visitedGroupIds, id];
    },
    /** 검토 진행 자동 저장 — 적용된 hiddenSeqs는 건드리지 않는다 */
    saveDraft() {
      if (!this.reportId || this.groups.length === 0) return;
      saveAiDefectDedupDraft(this.reportId, {
        thresholdMeters: this.thresholdMeters,
        groupIndex: this.groupIndex,
        visitedGroupIds: [...this.visitedGroupIds],
        keepSeqsByGroupId: { ...this.keepSeqsByGroupId }
      });
    },
    keptSeqsOfCurrentGroup() {
      if (!this.currentGroup) return [];
      return (this.keepSeqsByGroupId[this.currentGroup.id] || []).map(Number);
    },
    isKept(seq) {
      return this.keptSeqsOfCurrentGroup().includes(Number(seq));
    },
    /** 그룹에 남는 마지막 1개 — 해제 불가 */
    isLastKept(seq) {
      const kept = this.keptSeqsOfCurrentGroup();
      return kept.length === 1 && kept[0] === Number(seq);
    },
    /**
     * 그룹 전체가 오검출일 때 — 전원 숨김(빈 keep 목록)으로 전환.
     * 카드 클릭의 최소 1개 잠금과 달리 명시적 버튼으로만 진입한다.
     */
    toggleGroupNotDefect() {
      if (!this.currentGroup) return;
      const next = this.isGroupAllHidden
        ? [Number(this.currentGroup.recommendedSeq)]
        : [];
      this.$set(this.keepSeqsByGroupId, this.currentGroup.id, next);
      this.markCurrentGroupVisited();
      this.saveDraft();
    },
    toggleKeep(seq) {
      if (!this.currentGroup) return;
      const n = Number(seq);
      const kept = this.keptSeqsOfCurrentGroup();
      if (kept.includes(n)) {
        if (kept.length === 1) {
          this.addAlert({
            type: 'info',
            message: this.$t('map.aiDedupLastKeeperLock'),
            timeout: 3500
          });
          return;
        }
        this.$set(
          this.keepSeqsByGroupId,
          this.currentGroup.id,
          kept.filter(s => s !== n)
        );
      } else {
        this.$set(this.keepSeqsByGroupId, this.currentGroup.id, [...kept, n]);
      }
      this.markCurrentGroupVisited();
      this.saveDraft();
    },
    memberImageName(member) {
      return this.detailBySeq[member.seq]?.imageName || member.imgName || '';
    },
    formatPoints(coordinates) {
      if (!Array.isArray(coordinates)) return '';
      return coordinates
        .map(p => (Array.isArray(p) ? `${p[0]},${p[1]}` : `${p.x},${p.y}`))
        .join(' ');
    },
    memberViewBox(seq) {
      const coords = this.detailBySeq[seq]?.coordinates;
      if (!Array.isArray(coords) || coords.length === 0) {
        return `0 0 ${VIEW_W} ${VIEW_H}`;
      }
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      coords.forEach(p => {
        const x = Array.isArray(p) ? p[0] : p.x;
        const y = Array.isArray(p) ? p[1] : p.y;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      });
      const pad = Math.max(48, Math.max(maxX - minX, maxY - minY) * 0.75);
      const vbX = Math.max(0, minX - pad);
      const vbY = Math.max(0, minY - pad);
      const vbMaxX = Math.min(VIEW_W, maxX + pad);
      const vbMaxY = Math.min(VIEW_H, maxY + pad);
      return `${vbX} ${vbY} ${vbMaxX - vbX} ${vbMaxY - vbY}`;
    },
    /** 검토한 그룹(+이전 적용 그룹)만 대상 — 미검토·미적용 그룹은 계산에서 제외 */
    deleteReviewedDuplicates() {
      const seqs = this.reviewedPlannedHiddenSeqs;
      if (!seqs.length) {
        return;
      }
      const range = this.reviewedGroupNumberRange;
      const confirmKey =
        range.from === range.to
          ? 'map.aiDedupDeleteReviewedConfirmSingle'
          : 'map.aiDedupDeleteReviewedConfirm';
      if (
        !window.confirm(
          this.$t(confirmKey, {
            from: range.from,
            to: range.to,
            n: seqs.length
          })
        )
      ) {
        return;
      }
      this.deleteDuplicateSeqs(seqs, { skipConfirm: true });
    },
    deleteAllDuplicates() {
      if (!this._confirmUnreviewedForDelete()) {
        return;
      }
      const seqs = computeHiddenSeqs(this.groups, this.keepSeqsByGroupId, null);
      this.deleteDuplicateSeqs(seqs);
    },
    _confirmUnreviewedForDelete() {
      const unreviewed = this.groups.length - this.reviewedTargetGroupIds.size;
      if (unreviewed <= 0) {
        return true;
      }
      return window.confirm(
        this.$t('map.aiDedupDeleteAllConfirm', {
          n: unreviewed,
          total: this.groups.length
        })
      );
    },
    async deleteDuplicateSeqs(seqs, { skipConfirm = false } = {}) {
      const ids = Array.from(
        new Set(
          (seqs || [])
            .map(Number)
            .filter(n => Number.isFinite(n) && n > 0)
        )
      );
      if (!ids.length) {
        return;
      }
      if (
        !skipConfirm &&
        !window.confirm(
          this.$t('map.aiDedupDeleteConfirm', { n: ids.length })
        )
      ) {
        return;
      }

      this.loading = true;
      try {
        await moduleDefectApi.deleteModuleDefects(ids);
        ids.forEach(seq => {
          DotSolarMap.removeDefectMarkersBySeq(seq);
          invalidateModuleDefectDetailCache(seq);
        });

        const deletedSet = new Set(ids);
        const nextHidden = (this.appliedHiddenSeqs || []).filter(
          seq => !deletedSet.has(Number(seq))
        );
        saveAiDefectDedupState(this.reportId, {
          hiddenSeqs: nextHidden,
          thresholdMeters: this.thresholdMeters,
          draft: null
        });
        this.appliedHiddenSeqs = nextHidden;
        this.hasSavedState = nextHidden.length > 0;

        this.addAlert({
          type: 'success',
          message: this.$t('map.aiDedupDeleted', { n: ids.length }),
          timeout: 4000
        });
        this.$emit('deleted', { defectIds: ids });
        await this.fetchData();
      } catch (error) {
        console.error('[AiDefectDedupDialog] deleteDuplicateSeqs', error);
        this.addAlert({
          type: 'error',
          message: this.$t('map.aiDedupDeleteFailed'),
          timeout: 6000
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.ai-dedup-body {
  background-color: var(--v-background-base);
  min-height: 0;
}

.ai-dedup-toolbar {
  border-bottom: 1px solid var(--v-borderSubtle-base, rgba(255, 255, 255, 0.12));
}

.ai-dedup-scroll {
  overflow-x: hidden;
  overflow-y: auto;
  min-height: 0;
}

.ai-dedup-controls {
  gap: 8px;
}

.ai-dedup-group-actions {
  gap: 8px;
}

.ai-dedup-threshold-wrap {
  min-width: 160px;
  max-width: 220px;
}

.ai-dedup-threshold {
  width: 100%;
}

.ai-dedup-counter {
  min-width: 7.5rem;
  text-align: center;
}

.ai-dedup-cards {
  gap: 12px;
  width: 100%;
  min-width: 0;
}

.ai-dedup-card {
  width: 280px;
  max-width: 100%;
  cursor: pointer;
  overflow: hidden;
  transition: border-color var(--solvit-motion-duration) var(--solvit-motion-easing),
    box-shadow var(--solvit-motion-duration) var(--solvit-motion-easing),
    opacity var(--solvit-motion-duration) var(--solvit-motion-easing);
}

.ai-dedup-card--keeper {
  border: 2px solid var(--v-primary-base) !important;
}

.ai-dedup-card--hidden {
  opacity: 0.72;
}

.ai-dedup-card--hidden:hover {
  opacity: 1;
}

/* 마지막 남은 1개 — 해제 불가 표시 */
.ai-dedup-card--locked {
  cursor: default;
}

.ai-dedup-card__frame {
  width: 100%;
  aspect-ratio: 4 / 3;
  background: #111;
  overflow: hidden;
}

.ai-dedup-card__svg {
  display: block;
  width: 100%;
  height: 100%;
}

.ai-dedup-card__bbox {
  fill: transparent;
  stroke: #00e5ff;
  stroke-width: 2px;
  vector-effect: non-scaling-stroke;
  paint-order: stroke fill;
}

.ai-dedup-card__meta {
  min-width: 0;
}

.ai-dedup-footer-row {
  gap: 8px;
}

.ai-dedup-footer-label {
  min-width: 4.5rem;
}

.ai-dedup-footer {
  flex-shrink: 0;
}
</style>
